import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { Plus, Layers } from 'lucide-react'
import { Button } from '@trafiflow/shared-ui'
import { StepCard } from './StepCard'
import { EmptyState } from '../shared/EmptyState'
import type { FunnelStep } from '../../types'

interface StepListProps {
  steps: FunnelStep[]
  onReorder: (activeId: string, overId: string) => void
  onEdit: (stepId: string) => void
  onDelete: (stepId: string) => void
  onBuildPage: (stepId: string) => void
  onAddStep: () => void
  savingStepIds?: Set<string>
}

export function StepList({ steps, onReorder, onEdit, onDelete, onBuildPage, onAddStep, savingStepIds = new Set() }: StepListProps) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      onReorder(String(active.id), String(over.id))
    }
  }

  return (
    <div
      className="rounded-lg border p-4"
      style={{
        borderColor: 'var(--fn-border)',
        backgroundColor: 'var(--fn-bg-card)',
      }}
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-fn-text">
          Funnel Steps
        </h2>
        <Button variant="primary" className="text-sm" onClick={onAddStep}>
          <Plus className="w-4 h-4" />
          Add Step
        </Button>
      </div>

      {steps.length === 0 ? (
        <EmptyState
          icon={<Layers className="w-6 h-6 text-fn-text-muted" />}
          title="No steps configured"
          description="Add your first funnel step to get started"
          actionLabel="Add Step"
          onAction={onAddStep}
        />
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={steps.map((s) => s.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2" role="listbox" aria-label="Funnel steps list">
              {steps.map((step) => (
                <StepCard
                  key={step.id}
                  step={step}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onBuildPage={onBuildPage}
                  isSaving={savingStepIds.has(step.id)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  )
}
